import React, { lazy, Suspense } from 'react';
import { AdaptiveDashboardLayout } from '@/components/personalization/AdaptiveDashboardLayout';
import { useDashboardPersonalization } from '@/hooks/useDashboardPersonalization';
import { MetricsSection } from '../components/MetricsSection';
import { Skeleton } from '@/components/ui/skeleton';
import { MobileEssentials } from '../mobile/MobileEssentials';

// Lazy load heavy components for better performance
const OptimizedAIRunCoachTeaser = lazy(() => import('../widgets/OptimizedAIRunCoachTeaser').then(module => ({ default: module.OptimizedAIRunCoachTeaser })));
const OptimizedGeneralFitnessCoachTeaser = lazy(() => import('../widgets/OptimizedGeneralFitnessCoachTeaser').then(module => ({ default: module.OptimizedGeneralFitnessCoachTeaser })));
const NutritionSummaryCard = lazy(() => import('../NutritionSummaryCard').then(module => ({ default: module.NutritionSummaryCard })));
const NutritionCoachingTeaser = lazy(() => import('../NutritionCoachingTeaser').then(module => ({ default: module.NutritionCoachingTeaser })));

// Loading skeleton for AI coaches
const AICoachSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
    <Skeleton className="h-48 w-full" />
    <Skeleton className="h-48 w-full" />
  </div>
);


export const DashboardSections: React.FC = React.memo(() => {
  const { profile, isLoading } = useDashboardPersonalization();

  // Show loading state while personalization is being calculated
  if (isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Skeleton className="h-32 w-full" />
        <AICoachSkeleton />
      </div>
    );
  }

const dashboardSections = [
    {
      id: 'key_metrics',
      component: <MetricsSection />,
      priority: 1,
    },
    {
      id: 'nutrition_overview',
      component: (
        <>
          {/* Mobile variant replaces desktop nutrition cards in this slot */}
          <div className="md:hidden">
            <MobileEssentials />
          </div>

          {/* Desktop variant only visible from md and up */}
          <div className="hidden md:block">
            <Suspense fallback={<div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6"><Skeleton className="h-48 w-full" /><Skeleton className="h-48 w-full" /></div>}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <NutritionSummaryCard />
                <NutritionCoachingTeaser />
              </div>
            </Suspense>
          </div>
        </>
      ),
      priority: 2,
    },
    {
      id: 'ai_coaches',
      component: (
        <Suspense fallback={<AICoachSkeleton />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <OptimizedAIRunCoachTeaser />
            <OptimizedGeneralFitnessCoachTeaser />
          </div>
        </Suspense>
      ),
      priority: 3,
    },
  ];

  return (
    <AdaptiveDashboardLayout sections={dashboardSections} />
  );
});

DashboardSections.displayName = 'DashboardSections';